import { useState, useEffect, useRef } from 'react'
import {
  MicrophoneIcon,
  StopIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  SpeakerWaveIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'
import type { MockInterviewQuestion, MockInterviewSession, QuizCategory, QuizDifficulty } from '../types'
import { useSpeechRecognition } from '../hooks/useSpeechRecognition'
import {
  evaluateAnswer,
  textToSpeech,
  playAudioBlob,
  stopCurrentAudio,
  saveMockInterviewSession,
} from '../services/mockInterviewService'

type Phase = 'speaking' | 'listening' | 'evaluating' | 'feedback' | 'complete'

type Props = {
  userId: string
  questions: MockInterviewQuestion[]
  positionTitle: string
  techStack: string[]
  difficulty: QuizDifficulty
  categories: QuizCategory[]
  applicationId: string | null
  generalRole: string | null
  generalTech: string[] | null
  onFinish: (session: MockInterviewSession) => void
  onCancel: () => void
}

const SESSION_TIME_LIMIT = 30 * 60 // 30 minutes in seconds

const categoryColor: Record<string, string> = {
  technical: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  conceptual: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  behavioral: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function MockInterviewExecutor({
  userId,
  questions: initialQuestions,
  positionTitle,
  techStack,
  difficulty,
  categories,
  applicationId,
  generalRole,
  generalTech,
  onFinish,
  onCancel,
}: Props) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>('speaking')
  const [questions, setQuestions] = useState<MockInterviewQuestion[]>(initialQuestions)
  const [textInput, setTextInput] = useState('')
  const [isTTSPlaying, setIsTTSPlaying] = useState(false)
  const [ttsError, setTtsError] = useState<string | null>(null)
  const [evalError, setEvalError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Timer
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const startTimeRef = useRef(Date.now())
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Speech recognition
  const {
    transcript,
    isListening,
    isSupported: speechSupported,
    error: speechError,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognition()

  // Default to text mode when speech recognition is unavailable (e.g. Firefox)
  const [useTextMode, setUseTextMode] = useState(!speechSupported)

  const question = questions[currentIndex]
  const total = questions.length
  const progress = ((currentIndex + 1) / total) * 100
  const timeRemaining = SESSION_TIME_LIMIT - elapsedSeconds

  // TTS audio cache: pre-fetched blobs keyed by question index
  const ttsCacheRef = useRef<Map<number, Promise<Blob>>>(new Map())

  const prefetchTTS = (index: number) => {
    if (index >= total) return
    if (ttsCacheRef.current.has(index)) return
    const promise = textToSpeech(questions[index].text).catch(() => null as unknown as Blob)
    ttsCacheRef.current.set(index, promise)
  }

  // Pre-fetch first question's audio on mount
  useEffect(() => {
    prefetchTTS(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Session timer
  useEffect(() => {
    startTimeRef.current = Date.now()
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
      setElapsedSeconds(elapsed)
    }, 1000)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  // Auto-complete on time limit
  useEffect(() => {
    if (elapsedSeconds >= SESSION_TIME_LIMIT && phase !== 'complete') {
      handleCompleteSession()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsedSeconds])

  // Play TTS when entering speaking phase
  // Use a ref to prevent StrictMode double-fire from playing audio twice
  const ttsAbortRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (phase === 'speaking' && question) {
      // Cancel any in-flight TTS from a previous render
      if (ttsAbortRef.current) {
        ttsAbortRef.current()
        ttsAbortRef.current = null
      }

      let cancelled = false
      ttsAbortRef.current = () => { cancelled = true }

      setIsTTSPlaying(true)
      setTtsError(null)

      // Use pre-fetched audio if available, otherwise fetch now
      const audioPromise = ttsCacheRef.current.get(currentIndex) || textToSpeech(question.text)

      audioPromise
        .then(blob => {
          if (cancelled || !blob) return
          return playAudioBlob(blob)
        })
        .catch(err => {
          if (cancelled) return
          console.error('TTS error:', err)
          setTtsError('Could not play audio. You can read the question above.')
        })
        .finally(() => {
          if (!cancelled) setIsTTSPlaying(false)
        })

      return () => {
        cancelled = true
        ttsAbortRef.current = null
        stopCurrentAudio()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentIndex])

  const handleReadyToAnswer = () => {
    setPhase('listening')
    resetTranscript()
    setTextInput('')
    setEvalError(null)

    // Auto-start listening if speech is supported and not in text mode
    if (speechSupported && !useTextMode) {
      startListening()
    }
  }

  const handleSubmitAnswer = async () => {
    // Stop listening first
    if (isListening) {
      stopListening()
    }

    const answer = useTextMode ? textInput.trim() : transcript.trim()

    if (!answer) {
      setEvalError('Please provide an answer before submitting.')
      return
    }

    setPhase('evaluating')
    setEvalError(null)

    // Pre-fetch next question's TTS audio while evaluating
    prefetchTTS(currentIndex + 1)

    try {
      const evaluation = await evaluateAnswer({
        question: question.text,
        userAnswer: answer,
        positionTitle,
        techStack,
        difficulty,
        category: question.category,
        followUpHint: question.follow_up_hint,
      })

      // Update question with answer and feedback
      setQuestions(prev => prev.map((q, idx) =>
        idx === currentIndex
          ? {
              ...q,
              user_answer: answer,
              feedback: evaluation.feedback,
              score: evaluation.score,
              strengths: evaluation.strengths,
              improvements: evaluation.improvements,
              sample_answer_hint: evaluation.sample_answer_hint,
            }
          : q
      ))

      setPhase('feedback')

      // Play feedback TTS in background (non-blocking, non-critical)
      textToSpeech(evaluation.feedback)
        .then(blob => playAudioBlob(blob))
        .catch(() => { /* TTS for feedback is non-critical */ })
    } catch (err) {
      console.error('Evaluation error:', err)
      setEvalError(err instanceof Error ? err.message : 'Failed to evaluate your answer')
      setPhase('listening') // Go back to listening
    }
  }

  const handleNextQuestion = () => {
    // Stop any playing feedback audio before moving on
    stopCurrentAudio()

    if (currentIndex < total - 1) {
      setCurrentIndex(prev => prev + 1)
      setPhase('speaking')
      resetTranscript()
      setTextInput('')
    } else {
      handleCompleteSession()
    }
  }

  const handleSkipQuestion = () => {
    // Mark as skipped with no answer
    setQuestions(prev => prev.map((q, idx) =>
      idx === currentIndex
        ? { ...q, user_answer: null, feedback: 'Question was skipped.', score: 0 }
        : q
    ))
    handleNextQuestion()
  }

  const handleCompleteSession = () => {
    stopCurrentAudio()
    if (isListening) stopListening()
    setPhase('complete')
  }

  const handleSaveAndFinish = async () => {
    setIsSaving(true)

    const answeredQuestions = questions.filter(q => q.score !== null)
    const totalScore = answeredQuestions.length > 0
      ? Math.round(answeredQuestions.reduce((sum, q) => sum + (q.score || 0), 0) / answeredQuestions.length)
      : 0

    try {
      const session = await saveMockInterviewSession(
        userId,
        applicationId,
        generalRole,
        generalTech,
        difficulty,
        categories,
        questions,
        totalScore,
        elapsedSeconds,
      )
      onFinish(session)
    } catch (err) {
      console.error('Save error:', err)
      // Still try to finish
      onFinish({
        id: 'unsaved',
        user_id: userId,
        application_id: applicationId,
        general_role: generalRole,
        general_tech: generalTech,
        difficulty,
        categories,
        total_questions: total,
        score: totalScore,
        questions,
        duration_seconds: elapsedSeconds,
        created_at: new Date().toISOString(),
      })
    } finally {
      setIsSaving(false)
    }
  }

  const scoreColor = (score: number) =>
    score >= 80 ? 'text-emerald-500' :
    score >= 60 ? 'text-amber-500' :
    'text-rose-500'

  // Complete phase
  if (phase === 'complete') {
    const answeredQuestions = questions.filter(q => q.score !== null)
    const totalScore = answeredQuestions.length > 0
      ? Math.round(answeredQuestions.reduce((sum, q) => sum + (q.score || 0), 0) / answeredQuestions.length)
      : 0

    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="card p-8 text-center space-y-4">
          <CheckCircleIcon className="h-16 w-16 text-emerald-500 mx-auto" />
          <h2 className="text-2xl font-bold text-app-ink font-display">Interview Complete!</h2>

          <div className={`text-6xl font-bold ${scoreColor(totalScore)}`}>
            {totalScore}%
          </div>

          <p className="text-app-muted">
            {answeredQuestions.length} of {total} questions answered
          </p>

          <p className="text-sm text-app-muted">
            Duration: {formatTime(elapsedSeconds)}
          </p>

          {/* Quick category breakdown */}
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            {categories.map(cat => {
              const catQuestions = answeredQuestions.filter(q => q.category === cat)
              if (catQuestions.length === 0) return null
              const avgScore = Math.round(catQuestions.reduce((s, q) => s + (q.score || 0), 0) / catQuestions.length)
              return (
                <div
                  key={cat}
                  className="flex items-center gap-2 rounded-lg border border-app-line px-3 py-1.5"
                >
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor[cat] || ''}`}>
                    {cat}
                  </span>
                  <span className={`text-sm font-medium ${scoreColor(avgScore)}`}>
                    {avgScore}%
                  </span>
                </div>
              )
            })}
          </div>

          <div className="flex justify-center gap-3 pt-4">
            <button
              onClick={handleSaveAndFinish}
              disabled={isSaving}
              className="flex items-center gap-2 rounded-lg bg-app-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Saving...
                </>
              ) : (
                'Save & View Results'
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const currentAnswer = useTextMode ? textInput : transcript

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Timer + Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-app-muted">
            Question {currentIndex + 1} of {total}
          </span>
          <span className={`flex items-center gap-1.5 ${timeRemaining <= 300 ? 'text-rose-500' : 'text-app-muted'}`}>
            <ClockIcon className="h-4 w-4" />
            {formatTime(timeRemaining > 0 ? timeRemaining : 0)}
          </span>
        </div>
        <div className="h-2 rounded-full bg-app-subtle overflow-hidden">
          <div
            className="h-full rounded-full bg-sky-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question card */}
      <div className="card p-6 space-y-5">
        {/* Category tag */}
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor[question.category] || 'bg-app-subtle text-app-ink'}`}>
            {question.category}
          </span>
          {phase === 'speaking' && isTTSPlaying && (
            <span className="flex items-center gap-1.5 text-xs text-sky-500">
              <SpeakerWaveIcon className="h-4 w-4 animate-pulse" />
              Speaking...
            </span>
          )}
        </div>

        {/* Question text */}
        <h2 className="text-lg font-semibold text-app-ink leading-relaxed font-display">
          {question.text}
        </h2>

        {ttsError && (
          <p className="text-xs text-amber-600 dark:text-amber-400">{ttsError}</p>
        )}

        {/* SPEAKING phase */}
        {phase === 'speaking' && (
          <button
            onClick={handleReadyToAnswer}
            className="w-full rounded-lg bg-app-primary px-5 py-3 text-sm font-semibold text-white hover:bg-app-primary-hover transition-colors"
          >
            Ready to Answer
          </button>
        )}

        {/* LISTENING phase */}
        {phase === 'listening' && (
          <div className="space-y-4">
            {/* Mode toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-app-muted">
                {useTextMode ? 'Type your answer' : 'Speak your answer'}
              </span>
              {speechSupported && (
                <button
                  onClick={() => {
                    if (isListening) stopListening()
                    setUseTextMode(!useTextMode)
                  }}
                  className="flex items-center gap-1.5 text-xs text-app-accent hover:text-app-accent-hover"
                >
                  {useTextMode ? (
                    <>
                      <MicrophoneIcon className="h-4 w-4" />
                      Switch to voice
                    </>
                  ) : (
                    <>
                      <PencilSquareIcon className="h-4 w-4" />
                      Switch to text
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Text mode */}
            {useTextMode ? (
              <textarea
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Type your answer here..."
                rows={5}
                className="w-full rounded-lg border border-app-line bg-app-surface px-4 py-3 text-sm text-app-ink focus:border-app-accent focus:outline-none resize-none"
                autoFocus
              />
            ) : (
              <>
                {/* Voice mode */}
                {!speechSupported ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
                      Speech recognition is not supported in this browser.
                    </p>
                    <button
                      onClick={() => setUseTextMode(true)}
                      className="text-sm text-app-accent hover:text-app-accent-hover"
                    >
                      Switch to text mode
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Mic button */}
                    <div className="flex justify-center">
                      <button
                        onClick={isListening ? stopListening : startListening}
                        className={`rounded-full p-4 transition-all ${
                          isListening
                            ? 'bg-rose-500 hover:bg-rose-600 animate-pulse ring-4 ring-rose-200 dark:ring-rose-900/40'
                            : 'bg-app-primary hover:bg-app-primary-hover'
                        }`}
                      >
                        {isListening ? (
                          <StopIcon className="h-8 w-8 text-white" />
                        ) : (
                          <MicrophoneIcon className="h-8 w-8 text-white" />
                        )}
                      </button>
                    </div>

                    <p className="text-center text-xs text-app-muted">
                      {isListening ? 'Listening... Click to stop' : 'Click to start recording'}
                    </p>

                    {speechError && (
                      <p className="text-center text-xs text-rose-500">{speechError}</p>
                    )}

                    {/* Live transcript */}
                    {transcript && (
                      <div className="rounded-lg border border-app-line bg-app-subtle px-4 py-3">
                        <p className="text-xs text-app-muted mb-1">Your answer:</p>
                        <p className="text-sm text-app-ink whitespace-pre-wrap">{transcript}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {evalError && (
              <p className="text-sm text-rose-500">{evalError}</p>
            )}

            {/* Action buttons */}
            <div className="flex items-center justify-between gap-3">
              <button
                onClick={handleSkipQuestion}
                className="text-sm text-app-muted hover:text-app-ink transition-colors"
              >
                Skip question
              </button>

              <button
                onClick={handleSubmitAnswer}
                disabled={!currentAnswer.trim()}
                className="flex items-center gap-2 rounded-lg bg-app-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Submit Answer
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* EVALUATING phase */}
        {phase === 'evaluating' && (
          <div className="text-center py-8">
            <div className="animate-spin w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-app-muted font-medium">Evaluating your answer...</p>
            <p className="text-xs text-app-muted mt-1">This may take a few seconds</p>
          </div>
        )}

        {/* FEEDBACK phase */}
        {phase === 'feedback' && question.score !== null && (
          <div className="space-y-4">
            {/* Score */}
            <div className="flex items-center gap-4">
              <div className={`text-4xl font-bold ${scoreColor(question.score)}`}>
                {question.score}%
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-app-ink">
                  {question.score >= 80 ? 'Excellent answer!' :
                   question.score >= 60 ? 'Good answer with room to improve' :
                   question.score >= 40 ? 'Partial answer — keep practicing' :
                   'Needs work — review the feedback below'}
                </p>
              </div>
            </div>

            {/* Your answer */}
            {question.user_answer && (
              <div className="rounded-lg border border-app-line bg-app-subtle px-4 py-3">
                <p className="text-xs font-medium text-app-muted mb-1">Your answer:</p>
                <p className="text-sm text-app-ink whitespace-pre-wrap">{question.user_answer}</p>
              </div>
            )}

            {/* Feedback */}
            <div className="rounded-lg border border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-950/20 px-4 py-3">
              <p className="text-xs font-medium text-sky-700 dark:text-sky-400 mb-1">Feedback:</p>
              <p className="text-sm text-sky-900 dark:text-sky-200">{question.feedback}</p>
            </div>

            {/* Strengths */}
            {question.strengths.length > 0 && (
              <div>
                <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400 mb-1">Strengths:</p>
                <ul className="space-y-1">
                  {question.strengths.map((s, i) => (
                    <li key={i} className="text-sm text-app-ink flex items-start gap-2">
                      <span className="text-emerald-500 mt-0.5">✓</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Improvements */}
            {question.improvements.length > 0 && (
              <div>
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">Areas to improve:</p>
                <ul className="space-y-1">
                  {question.improvements.map((s, i) => (
                    <li key={i} className="text-sm text-app-ink flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">→</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sample answer hint */}
            {question.sample_answer_hint && (
              <div className="rounded-lg border border-app-line bg-app-subtle px-4 py-3">
                <p className="text-xs font-medium text-app-muted mb-1">Hint — ideal answer includes:</p>
                <p className="text-sm text-app-muted italic">{question.sample_answer_hint}</p>
              </div>
            )}

            {/* Next / Finish */}
            <div className="flex justify-end">
              <button
                onClick={handleNextQuestion}
                className="flex items-center gap-2 rounded-lg bg-app-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-app-primary-hover transition-colors"
              >
                {currentIndex < total - 1 ? (
                  <>
                    Next Question
                    <ArrowRightIcon className="h-4 w-4" />
                  </>
                ) : (
                  'Finish Interview'
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom: Cancel */}
      {phase !== 'evaluating' && (
        <div className="flex justify-between items-center">
          <button
            onClick={onCancel}
            className="text-sm text-app-muted hover:text-app-ink transition-colors"
          >
            Cancel Interview
          </button>

          {/* Question dots */}
          <div className="flex flex-wrap gap-1.5">
            {questions.map((q, idx) => (
              <div
                key={q.id}
                className={`h-2.5 w-2.5 rounded-full transition-all ${
                  idx === currentIndex
                    ? 'bg-sky-500 scale-125'
                    : q.score !== null
                      ? q.score >= 60 ? 'bg-emerald-400' : 'bg-amber-400'
                      : q.user_answer === null && idx < currentIndex
                        ? 'bg-app-surface'
                        : 'bg-app-line'
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
